/* ------------------------------------------------------------------
   ChampKoi product inquiry: "Email Us" / "Copy Info" on product pages.
   Builds a structured summary of the listing (title, selected variant,
   price, status, URL) so staff can identify exactly which item a
   customer is asking about. Koi also carry breeder + size; everything
   else (pond supplies, medication, apparel, shipping boxes) carries
   brand + SKU, and the wording drops the koi framing. The variant is
   resolved at click time from the ?variant= URL param the variant
   picker maintains.

   Signed-in customers also get their own details appended, but only to
   the email. Copied text gets pasted into public places (a Facebook
   comment under a koi photo, a forum thread), so the clipboard payload
   stays listing-only.
   ------------------------------------------------------------------ */

(() => {
  if (window.__productInquiryLoaded) return;
  window.__productInquiryLoaded = true;

  const buildDetails = (wrapper) => {
    const data = wrapper.dataset;
    let variants = [];
    try {
      variants = JSON.parse(wrapper.querySelector('[data-product-inquiry-variants]').textContent);
    } catch {
      /* fall through with no variant info */
    }

    const urlVariant = parseInt(new URLSearchParams(window.location.search).get('variant'), 10);
    const variant =
      variants.find((v) => v.id === urlVariant) ||
      variants.find((v) => v.id === parseInt(data.defaultVariant, 10)) ||
      variants[0] ||
      null;
    // data-kind is "product" for supplies, or the fish noun ("koi" /
    // "Tamasaba") for a live fish listing.
    const isKoi = data.kind !== 'product';
    // Only worth naming a variant when there is more than one to choose from;
    // single-variant supplies often just repeat the product title.
    const multiVariant = variants.length > 1 && variant !== null;

    const lines = [`${isKoi ? 'Listing' : 'Product'}: ${data.title}`];
    if (multiVariant && variant.title !== data.title) lines.push(`Option: ${variant.title}`);
    if (data.breeder) lines.push(`Breeder: ${data.breeder}`);
    if (data.brand) lines.push(`Brand: ${data.brand}`);
    if (data.size) lines.push(`Size: ${data.size}`);
    if (variant) {
      if (!isKoi && variant.sku) lines.push(`SKU: ${variant.sku}`);
      // compareAt is only emitted when it beats the current price, so its
      // presence alone means the listing is marked down.
      lines.push(`Price: ${variant.price}${variant.compareAt ? ` (was ${variant.compareAt})` : ''}`);
      lines.push(`Status: ${variant.available ? 'Available' : 'Sold out'}`);
    }
    lines.push(`Link: ${multiVariant ? `${data.url}?variant=${variant.id}` : data.url}`);
    return { lines, data, isKoi };
  };

  // Present only for a signed-in customer, and only ever read on the email
  // path. Missing fields are omitted rather than sent blank: a "Phone:" line
  // with nothing after it reads as a broken email, not as "no phone on file".
  const buildCustomerLines = (wrapper) => {
    const el = wrapper.querySelector('[data-product-inquiry-customer]');
    if (!el) return [];
    let c;
    try {
      c = JSON.parse(el.textContent);
    } catch {
      return [];
    }
    const labels = [
      ['name', 'Name'],
      ['email', 'Account email'],
      ['phone', 'Phone'],
      ['location', 'Location'],
      ['admin', 'Customer record'],
    ];
    return labels.filter(([key]) => c[key]).map(([key, label]) => `${label}: ${c[key]}`);
  };

  const CHECK_ICON =
    '<svg class="ck-check-icon" style="width:.9em;height:.68em" viewBox="0 0 12 9" aria-hidden="true" focusable="false"><path d="M1 4.6 4.4 8 11 1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  const flashLabel = (btn, text, showCheck = false) => {
    const label = btn.querySelector('.product-inquiry-btn__label');
    if (!label) return;
    if (btn.__labelTimer) return; // already flashing
    const original = label.textContent;
    label.textContent = text;
    if (showCheck) label.insertAdjacentHTML('beforeend', ` ${CHECK_ICON}`);
    btn.classList.add('is-copied');
    btn.__labelTimer = setTimeout(() => {
      label.textContent = original;
      btn.classList.remove('is-copied');
      btn.__labelTimer = null;
    }, 2000);
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.product-inquiry-email, .product-inquiry-copy');
    if (!btn) return;

    const wrapper = btn.closest('.product-inquiry-wrapper');
    if (!wrapper) return;
    const { lines, data, isKoi } = buildDetails(wrapper);

    if (btn.classList.contains('product-inquiry-email')) {
      const subject = `Question about ${data.title}`;
      const intro = `I have a question about this ${isKoi ? data.kind : 'item'}:`;
      const customerLines = buildCustomerLines(wrapper);
      // "My question:" stays last so the cursor lands where they type.
      const blocks = [`Hi ChampKoi,`, intro, lines.join('\r\n')];
      if (customerLines.length) blocks.push(`My details:\r\n${customerLines.join('\r\n')}`);
      blocks.push('My question:\r\n');
      const body = blocks.join('\r\n\r\n');
      window.location.href = `mailto:${data.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      return;
    }

    const text = lines.join('\n');
    let copied = false;
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch {
      // Clipboard API unavailable (http, older Safari) — textarea fallback.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }
      ta.remove();
    }
    flashLabel(btn, copied ? 'Copied' : 'Copy failed', copied);
  });
})();
